import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Star, ChevronDown, ChevronUp, MessageCircle,
  Clock, Leaf, Archive, ArrowRight, ClipboardCheck, Sparkles,
  Home, Heart, Building2, Phone, MapPin,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ContactFormWidget from '../components/ContactFormWidget';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { PHONE, PHONE_LINK, WHATSAPP, ADDRESS, P, WEB3FORMS_KEY } from '../constants';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [ctaPhone, setCtaPhone] = useState('');
  const [ctaService, setCtaService] = useState('');
  const [ctaStatus, setCtaStatus] = useState('idle');
  const sectionRef = useRef(null);
  const [sectionVisible, setSectionVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setSectionVisible(true); io.disconnect(); }
    }, { threshold: 0.08 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const handleCtaSubmit = async (e) => {
    e.preventDefault();
    setCtaStatus('loading');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: 'Rückruf anfordern – Wohnungsräumung Wien',
          Telefon: ctaPhone,
          Leistung: ctaService || 'Nicht angegeben',
          from_name: 'CTA-Formular',
        }),
      });
      const data = await res.json();
      setCtaStatus(data.success ? 'success' : 'error');
    } catch {
      setCtaStatus('error');
    }
  };

  const faqs = [
    { q: 'Was kostet eine Wohnungsräumung in Wien?', a: 'Keller ab €290, kleine Wohnung ab €490, größere Wohnung ab €890. Nach der kostenlosen Besichtigung erhalten Sie einen verbindlichen Fixpreis – ohne versteckte Kosten.' },
    { q: 'Wie schnell können Sie räumen?', a: 'In vielen Fällen noch am selben oder nächsten Tag. Notfälle behandeln wir bevorzugt. Rufen Sie uns an – wir finden gemeinsam den passenden Termin.' },
    { q: 'Was passiert mit brauchbaren Möbeln?', a: 'Gut erhaltene Gegenstände werden bei der Preisberechnung angerechnet oder können gespendet werden. Wertvolle Dinge werden direkt vom Gesamtpreis abgezogen.' },
    { q: 'Ist die Wohnung danach besenrein?', a: 'Ja. Besenreine Übergabe ist Teil unseres Standardservices – ohne Aufpreis. Immer. Keine Ausnahme.' },
    { q: 'Räumen Sie auch Keller und Dachböden?', a: 'Ja, Keller und Dachböden gehören zu unseren häufigsten Aufträgen. Egal wie voll oder eng – wir kümmern uns um alles.' },
    { q: 'Muss ich bei der Räumung anwesend sein?', a: 'Nicht zwingend. Viele Kunden geben uns den Schlüssel. Wir arbeiten vertrauenswürdig und übergeben nach der Räumung besenrein zurück.' },
  ];

  return (
    <>
      <SEOHead
        title="Wohnungsräumung Wien & Umgebung » Fixpreis ✓ Heute verfügbar"
        description="Professionelle Wohnungsräumung in Wien. ✓ Kostenlose Besichtigung ✓ Fixpreis-Garantie ✓ Besenreine Übergabe. Termine kurzfristig möglich. Jetzt anfragen!"
        keywords="Wohnungsräumung Wien, Entrümpelung Wien, Haushaltsauflösung Wien, Nachlassräumung, Kellerräumung"
        canonical="/"
      />

      <main>
        {/* ===== HERO: SPLIT SCREEN ===== */}
        <section className="relative overflow-hidden bg-[#1A1A2E]" style={{ minHeight: '96vh' }}>
          {/* Left panel: VORHER (dark, chaotic) */}
          <div className="absolute inset-0 lg:right-1/2">
            <img
              src={P.heroBefore}
              alt="Volle, chaotische Wohnung vor der Räumung Wien"
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A2E]/95 via-[#1A1A2E]/85 to-[#1A1A2E]/70" />
          </div>

          {/* Right panel: NACHHER (bright, clean) – desktop only */}
          <div className="hidden lg:block absolute inset-0 left-1/2">
            <img
              src={P.heroAfter}
              alt="Leere, saubere Wohnung nach der Räumung Wien"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-[#FAFAF8]/60 via-[#FAFAF8]/25 to-transparent" />
            {/* Dividing line */}
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-white/20" />
            {/* "Nachher" label */}
            <div className="absolute bottom-12 right-10 text-right">
              <p className="text-[#7C9A82] text-xs font-black uppercase tracking-widest mb-1">— Nachher</p>
              <p className="cabinet font-bold text-2xl text-white/90 leading-tight drop-shadow-lg">Sauber.<br />Leer.<br />Frei.</p>
            </div>
          </div>

          {/* Content grid */}
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-16 lg:py-0" style={{ minHeight: '96vh', display: 'flex', alignItems: 'center' }}>
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center w-full">
              {/* LEFT: headline + bullets + CTA */}
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-[#E8722A] text-white px-4 py-2 rounded text-xs font-black mb-6 uppercase tracking-wider">
                  <Clock size={13} /> Termine kurzfristig möglich
                </div>
                <h1 className="cabinet font-bold leading-none mb-4" style={{ fontSize: 'clamp(3rem, 7vw, 5.5rem)', letterSpacing: '-0.03em' }}>
                  Platz für<br />Neues.
                </h1>
                <p className="text-lg text-[#FAFAF8]/85 font-semibold mb-1">
                  Wohnungsräumung Wien. Fixpreis. In 24 Stunden.
                </p>
                <div className="space-y-2.5 mb-8">
                  {[
                    'Sie haben Besseres zu tun, als drei Wochen im Keller zu stehen.',
                    'Kostenlose Besichtigung – verbindlicher Fixpreis danach.',
                    'Wertanrechnung: Brauchbares wird vom Preis abgezogen.',
                    'Besenreine Übergabe. Immer. Kein Aufpreis.',
                  ].map((b) => (
                    <div key={b} className="flex items-start gap-2.5">
                      <CheckCircle size={17} className="text-[#E8722A] flex-shrink-0 mt-0.5" />
                      <span className="text-[#FAFAF8]/85 text-sm leading-snug">{b}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <a href={PHONE_LINK}
                    className="flex items-center gap-2 bg-white text-[#1A1A2E] font-black px-6 py-3.5 rounded-lg hover:bg-gray-100 transition-colors text-sm">
                    <Phone size={16} /> Jetzt anrufen
                  </a>
                  <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-[#25D366] text-white font-black px-6 py-3.5 rounded-lg hover:bg-[#20bd5a] transition-colors text-sm">
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                </div>
              </div>

              {/* RIGHT: Form card */}
              <div id="kontakt" className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8" data-testid="hero-contact-form">
                <div className="mb-5">
                  <h2 className="cabinet font-bold text-[#1A1A2E] leading-tight mb-1" style={{ fontSize: '1.25rem', letterSpacing: '-0.01em' }}>
                    Wie viel Platz brauchen Sie?
                  </h2>
                  <p className="text-gray-400 text-xs">Kein Risiko. Kostenlose Besichtigung. Verbindlicher Fixpreis.</p>
                </div>
                <ContactFormWidget />
              </div>
            </div>
          </div>
        </section>

        {/* ===== TRUST BAR ===== */}
        <section className="bg-white border-y border-gray-100 py-4" data-testid="trust-bar">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100">
              {[
                { val: '⭐ 4.9 / 5.0', label: 'Google Bewertung' },
                { val: '2.000+ Kunden', label: 'Zufrieden seit 2010' },
                { val: 'Fixpreisgarantie', label: 'Keine Nachforderungen' },
                { val: '24h Service', label: 'Notfälle bevorzugt' },
              ].map((t) => (
                <div key={t.label} className="py-3 px-4 text-center">
                  <div className="cabinet font-bold text-[#1A1A2E] text-base sm:text-lg leading-tight">{t.val}</div>
                  <div className="text-gray-400 text-xs mt-0.5">{t.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== INTRO – VORHER/NACHHER CASE ===== */}
        <section ref={sectionRef} className="py-16 lg:py-24 bg-[#FAFAF8]">

          {/* Upper: Eyebrow + Headline + Subtext */}
          <div className={`max-w-4xl mx-auto px-4 text-center mb-12 fade-up${sectionVisible ? ' visible' : ''}`}>
            <p className="text-[#E8722A] font-bold text-xs uppercase tracking-widest mb-4">
              Ihr Partner in Wien seit 2010
            </p>
            <h2 className="cabinet font-bold text-[#1A1A2E] leading-none mb-5" style={{ fontSize: 'clamp(2.2rem, 5vw, 4rem)', letterSpacing: '-0.03em' }}>
              Wir kommen.<br />
              Wir räumen.<br />
              <span className="text-[#E8722A]">Sie haben Ruhe.</span>
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
              Sie haben Besseres zu tun, als wochenlang im Keller Ihrer Eltern zu stehen. Dafür gibt's uns.
            </p>
          </div>

          {/* Main: Slider left + Case details right */}
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-[3fr_2fr] gap-10 lg:gap-14 items-start">

              {/* Left: Before/After Slider */}
              <div className={`fade-up${sectionVisible ? ' visible' : ''}`} style={{ transitionDelay: '0.1s' }}>
                <BeforeAfterSlider
                  beforeSrc={P.clutteredRoom}
                  afterSrc={P.emptyRoom}
                  beforeAlt="Vollgestellte Wohnung vor der Räumung Wien"
                  afterAlt="Leere, besenreine Wohnung nach der Räumung Wien"
                />
              </div>

              {/* Right: Case details */}
              <div className={`flex flex-col gap-5 pt-1 fade-up${sectionVisible ? ' visible' : ''}`} style={{ transitionDelay: '0.2s' }}>

                {/* Badge */}
                <span className="inline-block self-start bg-[#E8722A]/10 text-[#E8722A] text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide">
                  Nachlassräumung
                </span>

                {/* Project info */}
                <div>
                  <h3 className="cabinet font-semibold text-[#1A1A2E] text-xl lg:text-2xl leading-tight mb-1.5">
                    3-Zimmer-Wohnung, 1020 Wien
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Komplett geräumt & besenrein übergeben – in einem Tag.
                  </p>
                </div>

                {/* Detail points with stagger */}
                <div className="flex flex-col gap-3">
                  {[
                    { icon: <Clock size={15} />, text: 'Fertig in 6 Stunden' },
                    { icon: <CheckCircle size={15} />, text: 'Besenreine Übergabe' },
                    { icon: <Leaf size={15} />, text: '80% der Möbel gespendet' },
                  ].map((d, i) => (
                    <div
                      key={d.text}
                      className={`flex items-center gap-3 fade-up${sectionVisible ? ' visible' : ''}`}
                      style={{ transitionDelay: `${0.35 + i * 0.1}s` }}
                    >
                      <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#E8722A]/10 flex items-center justify-center text-[#E8722A]">
                        {d.icon}
                      </span>
                      <span className="text-gray-700 text-sm font-medium">{d.text}</span>
                    </div>
                  ))}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100" />

                {/* Quote */}
                <div>
                  <p className="text-gray-600 text-base italic leading-relaxed mb-3">
                    "Alles wurde respektvoll und schnell erledigt. Wir mussten uns um nichts kümmern."
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={13} className="text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <span className="text-gray-400 text-xs">— Maria K., Wien</span>
                  </div>
                </div>

                {/* Text link */}
                <a
                  href="#leistungen"
                  onClick={(e) => { e.preventDefault(); document.getElementById('leistungen')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="text-[#E8722A] text-sm font-bold hover:underline inline-flex items-center gap-1 self-start"
                >
                  Alle Leistungen ansehen <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>

          {/* Bottom CTAs */}
          <div className={`max-w-7xl mx-auto px-4 mt-12 flex flex-wrap gap-3 justify-center lg:justify-start fade-up${sectionVisible ? ' visible' : ''}`} style={{ transitionDelay: '0.5s' }}>
            <button
              onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#E8722A] hover:bg-[#d4651e] text-white font-black px-7 py-3.5 rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-md text-sm">
              Kostenlos anfragen
            </button>
            <a
              href={PHONE_LINK}
              className="flex items-center gap-2 border-2 border-gray-200 text-[#1A1A2E] font-black px-6 py-3.5 rounded-lg hover:border-gray-400 transition-all hover:-translate-y-0.5 hover:shadow-md text-sm">
              <Phone size={15} /> {PHONE}
            </a>
          </div>
        </section>

        {/* ===== SERVICES ===== */}
        <section id="leistungen" className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Was wir für Sie tun</p>
              <h2 className="cabinet font-bold text-[#1A1A2E]" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', letterSpacing: '-0.02em' }}>
                Leistungen
              </h2>
              <p className="text-gray-400 text-sm mt-2 max-w-xl mx-auto">
                Von der Kellerräumung bis zur vollständigen Haushaltsauflösung.
              </p>
            </div>

            {/* 6 Equal Service Cards */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
              {[
                { photo: P.clutteredRoom, afterPhoto: P.emptyRoom, title: 'Wohnungsräumung', desc: 'Wohnungen aller Größen – schnell, diskret, termingerecht. Besenrein inklusive.', slug: '/wohnungsraeumung-wien', backText: 'Vollständig geräumt & besenrein übergeben.' },
                { photo: P.movingBoxes, afterPhoto: P.worker1, title: 'Haushaltsauflösung', desc: 'Komplette Auflösung mit Wertanrechnung für brauchbare Gegenstände.', slug: '/haushaltsaufloesung-wien', backText: 'Inklusive Wertanrechnung für Möbel & Elektro.' },
                { photo: P.furniturePile, afterPhoto: P.boxesRack, title: 'Kellerräumung', desc: 'Keller und Dachböden – schnell geräumt, fachgerecht entsorgt.', slug: '/kellerraeumung-wien', backText: 'Keller ab €290 Fixpreis.' },
                { photo: P.worker1, afterPhoto: P.processBoxes, title: 'Nachlassräumung', desc: 'Diskret, respektvoll und ohne Zeitdruck. Wir helfen in schwierigen Momenten.', slug: '/nachlassraeumung-wien', backText: 'Diskret & einfühlsam. Kein Stress.' },
                { photo: P.clutteredBoxes, afterPhoto: P.emptyRoom, title: 'Messie-Wohnung', desc: 'Ohne Vorwürfe, ohne Druck. Professionell und vertraulich.', slug: '/messie-wohnung-wien', backText: 'Vertraulich & ohne Wertung.' },
                { photo: P.garbageTruck, title: 'Büroräumung', desc: 'Büros, Gewerbe & Betriebsobjekte – schnell geräumt, besenrein übergeben.', slug: '/bueroraeumung-wien' },
                { photo: P.atticBefore, title: 'Dachbodenräumung', desc: 'Jahrzehnte an Gerümpel – wir tragen alles runter und entsorgen fachgerecht.', slug: '/dachbodenraeumung-wien' },
              ].map((s) => (
                <Link
                  key={s.title}
                  to={s.slug}
                  className="relative rounded-2xl shadow-md overflow-hidden block group"
                  style={{ height: '288px' }}
                  data-testid={`service-card-${s.title}`}
                >
                  <img src={s.photo} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(26,26,46,0.88) 0%, rgba(26,26,46,0.3) 55%, transparent 100%)' }} />
                  <div className="absolute bottom-0 left-0 p-6">
                    <h3 className="cabinet font-bold text-white text-xl mb-1">{s.title}</h3>
                    <p className="text-gray-300 text-xs mb-3 leading-relaxed">{s.desc}</p>
                    <span className="inline-flex items-center gap-1 bg-[#E8722A] text-white font-bold text-xs px-3 py-1.5 rounded">
                      Mehr erfahren <ArrowRight size={11} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ===== WHY US ===== */}
        <section id="ueber-uns" className="py-14 lg:py-20 bg-[#FAFAF8]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-3">Professionelle Räumung</p>
                <h2 className="cabinet font-bold text-[#1A1A2E] mb-4 leading-none" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', letterSpacing: '-0.02em' }}>
                  Warum sich eine<br />professionelle Entrümpelung<br />lohnt
                </h2>
                <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                  Eine Räumung ist mehr als Zeug wegschmeißen. Sie ist ein Neuanfang – und den begleiten wir strukturiert, transparent und ohne Überraschungen.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: <MapPin size={20} />, title: 'Wien & Umgebung', desc: 'Alle 23 Bezirke + Klosterneuburg, Mödling, Schwechat' },
                    { icon: <Clock size={20} />, title: '24h Termine', desc: 'Notfälle behandeln wir bevorzugt' },
                    { icon: <Heart size={20} />, title: 'Diskret & vertraulich', desc: 'Kein Vorwurf, kein Stress. Nur Hilfe.' },
                    { icon: <Leaf size={20} />, title: 'Umweltgerecht', desc: 'Sortenreine Trennung & fachgerechte Entsorgung' },
                  ].map((u) => (
                    <div key={u.title} className="bg-white rounded-xl p-4 border border-gray-100 hover:bg-gray-50 transition-colors">
                      <div className="text-[#7C9A82] mb-2">{u.icon}</div>
                      <div className="font-black text-[#1A1A2E] text-sm mb-1">{u.title}</div>
                      <div className="text-gray-400 text-xs leading-relaxed">{u.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden h-72 lg:h-[440px] shadow-2xl">
                <img src={P.workersUnloading} alt="Professionelle Räumung Wien" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* ===== PROCESS ===== */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-[900px] mx-auto px-4">

            {/* Header */}
            <div className="text-center mb-8 lg:mb-14">
              <p className="text-[#E8722A] font-bold text-xs uppercase tracking-widest">EINFACH &amp; TRANSPARENT</p>
              <h2 className="font-bold text-2xl lg:text-3xl text-[#1A1A2E] mt-2">So läuft es ab</h2>
            </div>

            {/* Steps */}
            <div className="relative">

              {/* Desktop: horizontale Verbindungslinie */}
              <div
                className="hidden md:block absolute h-0.5 bg-[#E8722A] z-0 pointer-events-none"
                style={{ top: '32px', left: 'calc(16.67% + 32px)', right: 'calc(16.67% + 32px)' }}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8" data-testid="process-steps">
                {[
                  { num: '01', Icon: Phone, title: 'Anfrage senden', desc: 'Formular ausfüllen oder anrufen. Kostenlos und unverbindlich.' },
                  { num: '02', Icon: ClipboardCheck, title: 'Besichtigung & Fixpreis', desc: 'Wir kommen vorbei und nennen Ihnen einen verbindlichen Festpreis.' },
                  { num: '03', Icon: Sparkles, title: 'Räumung & Übergabe', desc: 'Wir räumen, entsorgen und übergeben besenrein.' },
                ].map(({ num, Icon, title, desc }) => (
                  <div key={num}
                    className="flex md:flex-col items-center gap-4 md:gap-0 text-left md:text-center relative z-10 bg-[#FAFAF8] md:bg-transparent border border-gray-100 md:border-0 rounded-2xl md:rounded-none p-4 md:p-0"
                    data-testid={`process-step-${num}`}>
                    {/* Icon-Kreis */}
                    <div className="flex-shrink-0 group w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-[#E8722A] bg-white flex items-center justify-center transition-all duration-300 hover:bg-[#E8722A] cursor-default relative z-10">
                      <Icon size={24} className="text-[#E8722A] group-hover:text-white transition-colors duration-300" />
                    </div>
                    {/* Inhalt */}
                    <div>
                      <p className="text-[#E8722A] text-xs font-bold mb-0.5 md:mt-4">{num}</p>
                      <h3 className="font-bold text-base md:text-xl text-[#1A1A2E] md:mt-2">{title}</h3>
                      <p className="text-sm text-gray-500 mt-1 md:mt-2 leading-snug">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <button
                onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="process-cta-button"
                className="bg-[#E8722A] text-white font-bold py-3 px-8 rounded-lg hover:scale-[1.02] transition-transform">
                Kostenlos anfragen →
              </button>
            </div>

          </div>
        </section>

        {/* ===== PRICES – light ===== */}
        <section id="preise" className="py-14 lg:py-20 bg-[#FAFAF8]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-3">Transparente Kalkulation</p>
                <h2 className="cabinet font-bold text-[#1A1A2E] mb-2" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', letterSpacing: '-0.02em' }}>
                  Preise &amp; Transparenz
                </h2>
                <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                  Richtwerte zur Orientierung. Verbindlicher Fixpreis nach der kostenlosen Besichtigung.
                </p>
                <div className="space-y-3.5 mb-8">
                  {[
                    { title: 'Keller / Dachboden', price: 'ab €290', icon: <Archive size={20} />, highlight: false },
                    { title: '1–2 Zimmer Wohnung', price: 'ab €490', icon: <Home size={20} />, highlight: true },
                    { title: '3+ Zimmer Wohnung', price: 'ab €890', icon: <Building2 size={20} />, highlight: false },
                  ].map((pr) => (
                    <div
                      key={pr.title}
                      className={`pricing-card group relative flex items-center justify-between p-4 rounded-xl border-2 transition-all hover:-translate-y-0.5 overflow-hidden ${
                        pr.highlight ? 'border-[#E8722A] bg-[#E8722A]/5 shadow-sm' : 'border-gray-100 bg-[#FAFAF8]'
                      }`}
                    >
                      {/* Boxes bg that fades on hover */}
                      <div
                        className="pricing-boxes-bg absolute inset-0 bg-cover bg-right-bottom opacity-[0.06]"
                        style={{ backgroundImage: `url('${P.pricingBoxes}')` }}
                      />
                      <div className="relative flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${pr.highlight ? 'bg-[#E8722A] text-white' : 'bg-[#1A1A2E]/10 text-[#1A1A2E]'}`}>
                          {pr.icon}
                        </div>
                        <div>
                          <div className="font-black text-[#1A1A2E] text-sm">{pr.title}</div>
                          <div className="text-gray-400 text-xs flex items-center gap-1">
                            <CheckCircle size={11} className="text-[#7C9A82]" /> Besenrein &amp; Entsorgung inklusive
                          </div>
                        </div>
                      </div>
                      <div className="relative font-black text-4xl text-[#E8722A] leading-none">{pr.price}</div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full bg-[#E8722A] hover:bg-[#d4651e] text-white font-black py-4 rounded-xl transition-colors text-base">
                  Preis für Ihr Objekt anfragen
                </button>
                <p className="text-xs text-gray-400 mt-3 text-center">* Exakter Fixpreis nach kostenloser Besichtigung.</p>
                {/* WhatsApp-Hinweis */}
                <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left" data-testid="pricing-whatsapp-hint">
                  <MessageCircle size={20} className="flex-shrink-0" style={{ color: '#25D366' }} />
                  <span className="text-sm text-gray-500">Kleinauftrag? Foto per WhatsApp schicken und sofort einen Preis erhalten.</span>
                  <a
                    href={WHATSAPP}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pricing-whatsapp-button"
                    className="flex-shrink-0 text-sm text-white font-semibold py-2 px-4 rounded-lg hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#25D366' }}
                  >
                    WhatsApp öffnen
                  </a>
                </div>
              </div>
              <div className="hidden lg:block rounded-2xl overflow-hidden shadow-xl h-72 lg:h-[480px]">
                <img src={P.worker1} alt="Preise Wohnungsräumung Wien" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* ===== STATS ===== */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
              {[
                { num: '2.000+', label: 'Räumungen in Wien' },
                { num: '4.9★', label: 'Google Bewertung' },
                { num: '15+', label: 'Jahre Erfahrung' },
                { num: '72h', label: 'Ø Dauer bis Abschluss' },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-3xl sm:text-5xl font-bold leading-none mb-3" style={{ color: '#E8722A' }}>{s.num}</div>
                  <div className="text-sm text-gray-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== REVIEWS – light ===== */}
        <section id="bewertungen" className="py-14 lg:py-20 bg-[#FAFAF8]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Was unsere Kunden sagen</p>
              <h2 className="cabinet font-bold text-[#1A1A2E] mb-3" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', letterSpacing: '-0.02em' }}>
                Kundenbewertungen
              </h2>
              {/* Google Rating Bar */}
              <div className="inline-flex items-center gap-3 bg-white px-5 py-3 rounded-xl shadow-sm border border-gray-100">
                <svg width="22" height="22" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((i) => <Star key={i} size={17} className="text-yellow-400 fill-yellow-400" />)}
                </div>
                <span className="font-black text-[#1A1A2E] text-base">4.9</span>
                <span className="text-gray-400 text-sm">/ 200+ Bewertungen</span>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-5 mb-8">
              {[
                { name: 'Maria K.', loc: '1030 Wien', initials: 'MK', color: 'from-blue-500 to-indigo-600', text: 'Schnell, sauber und fair. Wohnung meiner Mutter in einem Tag komplett geräumt. Keine Überraschungen beim Preis.', timeAgo: 'vor 2 Wochen', stars: 5 },
                { name: 'Thomas B.', loc: '1210 Wien', initials: 'TB', color: 'from-emerald-500 to-teal-600', text: 'Endlich ein Unternehmen, das hält was es verspricht. Fixpreis eingehalten, alles besenrein übergeben. Sehr empfehlenswert!', timeAgo: 'vor 1 Monat', stars: 5 },
                { name: 'Andrea M.', loc: '1150 Wien', initials: 'AM', color: 'from-orange-400 to-rose-500', text: 'Professionell und diskret – Messie-Wohnung geräumt ohne Vorwürfe. Kein Aufpreis, super Arbeit. Danke für das Einfühlungsvermögen!', timeAgo: 'vor 3 Wochen', stars: 5 },
              ].map((r) => (
                <div key={r.name} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  {/* Google-style header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${r.color} flex items-center justify-center text-white text-xs font-black flex-shrink-0`}>
                        {r.initials}
                      </div>
                      <div>
                        <div className="font-black text-[#1A1A2E] text-sm leading-tight">{r.name}</div>
                        <div className="text-gray-400 text-sm">{r.loc}</div>
                        <div className="text-gray-400 text-xs">{r.timeAgo}</div>
                      </div>
                    </div>
                    <svg width="18" height="18" viewBox="0 0 24 24" className="flex-shrink-0 opacity-60 mt-0.5">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  {/* Stars */}
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: r.stars }).map((_, i) => (
                      <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-gray-400">
              <a href="https://www.google.com/search?q=Wohnungsräumung+Wien" target="_blank" rel="noopener noreferrer"
                className="text-[#E8722A] hover:underline font-semibold">
                Alle Bewertungen auf Google ansehen →
              </a>
            </p>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="py-14 lg:py-20 bg-white" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Antworten auf Ihre Fragen</p>
              <h2 className="cabinet font-bold text-[#1A1A2E]" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', letterSpacing: '-0.02em' }}>
                Häufig gestellte Fragen
              </h2>
            </div>
            <div className="space-y-2">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`faq-item-${idx}`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-gray-50/70 transition-colors">
                    <span className="font-black text-[#1A1A2E] text-sm sm:text-base leading-snug">{faq.q}</span>
                    {openFaq === idx
                      ? <ChevronUp size={18} className="text-[#E8722A] flex-shrink-0" />
                      : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
                  </button>
                  <div className={`faq-content ${openFaq === idx ? 'open' : ''}`}>
                    <div className="px-6 pb-5 border-t border-gray-50">
                      <p className="text-gray-500 text-sm leading-relaxed pt-4">{faq.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== HOURS – light ===== */}
        <section className="py-10 bg-[#FAFAF8] border-t border-[#E8722A]/20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Erreichbarkeit</p>
            <h2 className="cabinet font-bold text-[#1A1A2E] mb-6" style={{ fontSize: '1.5rem', letterSpacing: '-0.01em' }}>
              Öffnungszeiten
            </h2>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-[#FAFAF8] rounded-2xl p-6 border border-gray-100">
                <Clock size={26} className="text-[#E8722A] mx-auto mb-3" />
                <div className="font-black text-[#1A1A2E] mb-1">Montag – Samstag</div>
                <div className="text-gray-500 text-sm font-semibold">07:00 – 20:00 Uhr</div>
              </div>
              <div className="bg-[#FAFAF8] rounded-2xl p-6 border border-gray-100">
                <Phone size={26} className="text-[#E8722A] mx-auto mb-3" />
                <div className="font-black text-[#1A1A2E] mb-1">Telefon &amp; WhatsApp</div>
                <a href={PHONE_LINK} className="text-[#E8722A] font-black text-sm hover:underline">{PHONE}</a>
              </div>
              <div className="bg-[#FAFAF8] rounded-2xl p-6 border border-gray-100">
                <MapPin size={26} className="text-[#E8722A] mx-auto mb-3" />
                <div className="font-black text-[#1A1A2E] mb-1">Standort Wien</div>
                <div className="text-gray-500 text-xs leading-relaxed">{ADDRESS}</div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <h2 className="cabinet font-bold text-[#1A1A2E] mb-5 leading-none" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', letterSpacing: '-0.03em' }}>
              Räumung nötig?{' '}
              <span className="text-[#E8722A]">Wir sind morgen da.</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-5 mb-8 text-gray-500 text-sm">
              {['Kostenlose Besichtigung', 'Fixpreisgarantie', 'Besenreine Übergabe'].map((t) => (
                <span key={t} className="flex items-center gap-2">
                  <CheckCircle size={15} className="text-[#7C9A82]" />
                  <span className="font-semibold">{t}</span>
                </span>
              ))}
            </div>

            {ctaStatus === 'success' ? (
              <div className="bg-[#7C9A82]/10 border border-[#7C9A82]/30 rounded-2xl p-8">
                <CheckCircle size={36} className="text-[#7C9A82] mx-auto mb-3" />
                <p className="text-[#1A1A2E] font-bold text-lg">Wir melden uns kurzfristig!</p>
                <p className="text-gray-500 text-sm mt-1">Ihr Rückrufwunsch ist bei uns eingegangen.</p>
              </div>
            ) : (
              <form onSubmit={handleCtaSubmit} className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <input
                    type="tel"
                    value={ctaPhone}
                    onChange={(e) => setCtaPhone(e.target.value)}
                    placeholder="Ihre Telefonnummer"
                    required
                    data-testid="cta-phone-input"
                    className="flex-1 bg-white text-[#1A1A2E] placeholder-gray-400 px-4 py-3 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#E8722A] border border-gray-200"
                  />
                  <select
                    value={ctaService}
                    onChange={(e) => setCtaService(e.target.value)}
                    data-testid="cta-service-select"
                    className="flex-1 bg-white text-[#1A1A2E] px-4 py-3 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#E8722A] cursor-pointer border border-gray-200"
                  >
                    <option value="">Was soll geräumt werden?</option>
                    <option value="Wohnungsräumung">Wohnungsräumung</option>
                    <option value="Haushaltsauflösung">Haushaltsauflösung</option>
                    <option value="Nachlassräumung">Nachlassräumung</option>
                    <option value="Messie-Wohnung">Messie-Wohnung</option>
                    <option value="Kellerräumung">Kellerräumung</option>
                    <option value="Büroräumung">Büroräumung</option>
                    <option value="Dachbodenräumung">Dachbodenräumung</option>
                    <option value="Entrümpelung">Entrümpelung</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={ctaStatus === 'loading'}
                  data-testid="cta-callback-button"
                  className="w-full bg-[#E8722A] hover:bg-[#d4651e] disabled:opacity-60 text-white font-bold py-3 px-8 rounded-lg transition-colors text-base">
                  {ctaStatus === 'loading' ? 'Wird gesendet…' : 'Rückruf anfordern →'}
                </button>
                {ctaStatus === 'error' && (
                  <p className="text-red-500 text-xs mt-2 text-center">Fehler beim Senden. Bitte rufen Sie uns direkt an.</p>
                )}
              </form>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
